import { defineComponent as _defineComponent } from 'vue';
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createElementBlock as _createElementBlock, renderSlot as _renderSlot, Fragment as _Fragment } from "vue";
const _hoisted_1 = {
    key: 3,
    class: "flex flex-col gap-4",
    "data-testid": "annual-receipt-cancelled"
};
const _hoisted_2 = { class: "flex flex-wrap items-center justify-between gap-3" };
const _hoisted_3 = { class: "flex flex-col gap-1 text-sm text-(--colors-text-secondary)" };
const _hoisted_4 = { "data-testid": "annual-receipt-cancelled-message" };
const _hoisted_5 = {
    key: 0,
    "data-testid": "annual-receipt-cancellation-reason"
};
const _hoisted_6 = {
    key: 4,
    class: "flex flex-col gap-4"
};
const _hoisted_7 = {
    class: "text-sm text-(--colors-text-secondary)",
    "data-testid": "annual-receipt-pending"
};
const _hoisted_8 = ["data-testid"];
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { Button } from "@eudonet/opal";
import ModuleLoading from "@/shared/components/ModuleLoading.vue";
import ModuleState from "@/shared/components/ModuleState.vue";
import { formatDate } from "@/shared/utils/date-formatting";
import { useAppToast } from "@/shared/composables/useAppToast";
import { getApiErrorCode } from "@/infrastructure/services/ApiErrorService";
import { useProfileStore } from "@/modules/settings/public";
import TaxReceiptCard from "./TaxReceiptCard.vue";
import TaxReceiptCancelModal from "./TaxReceiptCancelModal.vue";
import DonorReceiptInformationNotice from "../DonorReceiptInformationNotice.vue";
import { useDonorReceiptInformation } from "../../composables/useDonorReceiptInformation";
import { cancelTaxReceipt, getAnnualTaxReceiptStatus, regenerateTaxReceipt, } from "../../../infrastructure/api/donationApi";
import { TAX_RECEIPT_CANCELLATION_REASON_CODES, } from "../../../domain/entities/TaxReceipt";
export default /*@__PURE__*/ _defineComponent({
    __name: 'AnnualTaxReceiptPanel',
    props: {
        donationId: {},
        donationStatus: { default: undefined },
        currency: { default: undefined },
        contactId: { default: undefined },
        organizationId: { default: undefined },
        receiptsOnly: { type: Boolean, default: false }
    },
    emits: ["update:loading", "download", "receipt-updated"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        const MESSAGE_KEYS = {
            NotValidated: "finance.donations.taxReceipt.annual.notValidated",
            NoAutomaticCampaign: "finance.donations.taxReceipt.annual.noCampaign",
            NotCovered: "finance.donations.taxReceipt.annual.notCovered",
        };
        const CANCEL_ERROR_KEYS = {
            TaxReceiptInvalidTransition: "finance.donations.taxReceipt.cancelErrorConflict",
            "TaxReceipt.CancellationReasonCode.Required": "finance.donations.taxReceipt.cancelModal.motifRequired",
            "TaxReceipt.CancellationReasonCode.Invalid": "finance.donations.taxReceipt.cancelModal.motifInvalid",
            "TaxReceipt.CancellationReason.Required": "finance.donations.taxReceipt.cancelModal.commentRequired",
            "TaxReceipt.CancellationReason.TooLong": "finance.donations.taxReceipt.cancelModal.commentTooLong",
        };
        // NotAnnual / NotCancelled cannot come from this card; they fall back to the generic message.
        const REGENERATE_ERROR_KEYS = {
            "TaxReceipt.Regeneration.NoEligibleDonation": "finance.donations.taxReceipt.regenerateErrors.noEligibleDonation",
            "TaxReceipt.Regeneration.AlreadyReplaced": "finance.donations.taxReceipt.regenerateErrors.alreadyReplaced",
            "TaxReceipt.AnnualJobAlreadyRunning": "finance.donations.taxReceipt.regenerateErrors.annualJobRunning",
            TaxReceiptIssuerInfoMissing: "finance.donations.taxReceipt.generateErrorIssuerInfoMissing",
            OpusLegalInfoUnavailable: "finance.donations.taxReceipt.generateErrorIssuerUnavailable",
        };
        const { t, locale } = useI18n();
        const profileStore = useProfileStore();
        const { toastSuccess, toastError } = useAppToast();
        const annual = ref(null);
        const isLoading = ref(false);
        const loadError = ref(false);
        const showCancelModal = ref(false);
        const isCancelling = ref(false);
        const isRegenerating = ref(false);
        // A status this build does not know renders nothing rather than a wrong sentence.
        const messageKey = computed(() => {
            const status = annual.value?.status;
            return status && status in MESSAGE_KEYS ? MESSAGE_KEYS[status] : null;
        });
        const cancellationReasonLabel = computed(() => {
            const receipt = annual.value?.receipt;
            const code = receipt?.cancellationReasonCode;
            if (!code || !TAX_RECEIPT_CANCELLATION_REASON_CODES.includes(code))
                return null;
            const label = t(`finance.donations.taxReceipt.cancelModal.motif.${code}`);
            return code === "Other" && receipt?.cancellationReason ? `${label} — ${receipt.cancellationReason}` : label;
        });
        const receiptInformation = useDonorReceiptInformation({
            contactId: computed(() => props.contactId),
            organizationId: computed(() => props.organizationId),
            enabled: computed(() => !props.receiptsOnly && annual.value?.status === "Pending"),
        });
        // Only the newest request may write: the donation or its status can change before OPDON answers.
        let latestRequestId = 0;
        async function load() {
            if (!props.donationId)
                return;
            const requestId = ++latestRequestId;
            setLoading(true);
            loadError.value = false;
            try {
                const result = await getAnnualTaxReceiptStatus(props.donationId);
                if (requestId !== latestRequestId)
                    return;
                annual.value = result;
                // The host reads one receipt for two rules: the edit lock ignores a Cancelled one, the deletion
                // rule counts it (kept on record, still listing the donation) — so both verdicts hand it over.
                const listsDonation = result.status === "Covered" || result.status === "Cancelled";
                emit("receipt-updated", listsDonation ? result.receipt : null);
            }
            catch {
                if (requestId === latestRequestId)
                    loadError.value = true;
            }
            finally {
                if (requestId === latestRequestId)
                    setLoading(false);
            }
        }
        function setLoading(loading) {
            isLoading.value = loading;
            emit("update:loading", loading);
        }
        function errorKey(err, overrides, fallback) {
            const code = getApiErrorCode(err);
            return code && overrides[code] ? overrides[code] : fallback;
        }
        async function handleCancel(reasonCode, reason) {
            const target = annual.value?.status === "Covered" ? annual.value.receipt : null;
            if (!target || isCancelling.value)
                return;
            isCancelling.value = true;
            try {
                await cancelTaxReceipt(target.taxReceiptId, reasonCode, reason);
                showCancelModal.value = false;
                toastSuccess("finance.donations.taxReceipt.annual.cancelSuccess");
                // Re-read rather than patch: the panel switches to the cancelled card and its regeneration.
                await load();
            }
            catch (err) {
                toastError(errorKey(err, CANCEL_ERROR_KEYS, "finance.donations.taxReceipt.cancelError"));
            }
            finally {
                isCancelling.value = false;
            }
        }
        async function handleRegenerate() {
            const target = annual.value?.status === "Cancelled" ? annual.value.receipt : null;
            if (!target || isRegenerating.value)
                return;
            isRegenerating.value = true;
            try {
                await regenerateTaxReceipt(target.taxReceiptId);
                toastSuccess("finance.donations.taxReceipt.regenerateSuccess");
            }
            catch (err) {
                toastError(errorKey(err, REGENERATE_ERROR_KEYS, "finance.donations.taxReceipt.regenerateError"));
            }
            finally {
                isRegenerating.value = false;
            }
            // Re-read on both outcomes: a refusal can mean another user already replaced the receipt.
            await load();
        }
        onMounted(load);
        watch(() => [props.donationId, props.donationStatus], load);
        // The host's spinner follows `update:loading`: leaving mid-request must not leave it on.
        onUnmounted(() => {
            latestRequestId += 1;
            if (isLoading.value)
                emit("update:loading", false);
        });
        return (_ctx, _cache) => {
            return (_openBlock(), _createElementBlock(_Fragment, null, [
                (isLoading.value)
                    ? (_openBlock(), _createBlock(ModuleLoading, { key: 0 }))
                    : (loadError.value)
                        ? (_openBlock(), _createBlock(ModuleState, {
                            key: 1,
                            type: "error",
                            title: _unref(t)('finance.donations.taxReceipt.loadError'),
                            "cta-icon": "ri-refresh-line",
                            "cta-label": _unref(t)('finance.donations.taxReceipt.retry'),
                            "data-testid": "annual-receipt-load-error",
                            onCta: load
                        }, null, 8 /* PROPS */, ["title", "cta-label"]))
                        : (annual.value?.status === 'Covered' && annual.value.receipt)
                            ? (_openBlock(), _createBlock(TaxReceiptCard, {
                                key: 2,
                                receipt: annual.value.receipt,
                                currency: __props.currency,
                                cancelling: isCancelling.value,
                                readonly: "",
                                cancellable: "",
                                onDownload: _cache[0] || (_cache[0] = ($event) => (emit('download', $event))),
                                onCancel: _cache[1] || (_cache[1] = ($event) => (showCancelModal.value = true))
                            }, null, 8 /* PROPS */, ["receipt", "currency", "cancelling"]))
                            : (annual.value?.status === 'Cancelled' && annual.value.receipt)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
                                    _createVNode(TaxReceiptCard, {
                                        receipt: annual.value.receipt,
                                        currency: __props.currency,
                                        readonly: "",
                                        onDownload: _cache[2] || (_cache[2] = ($event) => (emit('download', $event)))
                                    }, null, 8 /* PROPS */, ["receipt", "currency"]),
                                    _createElementVNode("div", _hoisted_2, [
                                        _createElementVNode("div", _hoisted_3, [
                                            _createElementVNode("p", _hoisted_4, _toDisplayString(_unref(t)("finance.donations.taxReceipt.annual.cancelled", { year: annual.value.fiscalYear })), 1 /* TEXT */),
                                            (cancellationReasonLabel.value)
                                                ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(_unref(t)("finance.donations.taxReceipt.annual.cancellationReason", { reason: cancellationReasonLabel.value })), 1 /* TEXT */))
                                                : _createCommentVNode("v-if", true)
                                        ]),
                                        _createVNode(_unref(Button), {
                                            variant: "primary",
                                            size: "sm",
                                            "icon-left": "ri-refresh-line",
                                            label: _unref(t)('finance.donations.taxReceipt.regenerate'),
                                            loading: isRegenerating.value,
                                            "data-testid": "annual-receipt-regenerate",
                                            onClick: handleRegenerate
                                        }, null, 8 /* PROPS */, ["label", "loading"])
                                    ])
                                ]))
                                : (!__props.receiptsOnly && annual.value?.status === 'Pending')
                                    ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                                        _createElementVNode("p", _hoisted_7, _toDisplayString(_unref(t)("finance.donations.taxReceipt.annual.pending", {
                                            year: annual.value.fiscalYear,
                                            date: _unref(formatDate)(annual.value.expectedGenerationDate ?? undefined, _unref(locale), _unref(profileStore).profile?.dateFormat),
                                        })), 1 /* TEXT */),
                                        _createVNode(DonorReceiptInformationNotice, {
                                            gaps: _unref(receiptInformation).issuerGaps.value,
                                            unavailable: false,
                                            "heading-key": "finance.donations.create.receiptInformation.workspaceTitle"
                                        }, null, 8 /* PROPS */, ["gaps"]),
                                        _createVNode(DonorReceiptInformationNotice, {
                                            gaps: _unref(receiptInformation).donorGaps.value,
                                            unavailable: _unref(receiptInformation).unavailable.value,
                                            "contact-id": __props.contactId,
                                            "organization-id": __props.organizationId,
                                            "on-retry": _unref(receiptInformation).retry
                                        }, null, 8 /* PROPS */, ["gaps", "unavailable", "contact-id", "organization-id", "on-retry"])
                                    ]))
                                    : (!__props.receiptsOnly && messageKey.value)
                                        ? (_openBlock(), _createElementBlock("p", {
                                            key: 5,
                                            class: "text-sm text-(--colors-text-secondary)",
                                            "data-testid": `annual-receipt-${annual.value?.status}`
                                        }, _toDisplayString(_unref(t)(messageKey.value)), 9 /* TEXT, PROPS */, _hoisted_8))
                                        : (__props.receiptsOnly && annual.value)
                                            ? _renderSlot(_ctx.$slots, "empty", { key: 6 })
                                            : _createCommentVNode("v-if", true),
                _createVNode(TaxReceiptCancelModal, {
                    modelValue: showCancelModal.value,
                    "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event) => ((showCancelModal).value = $event)),
                    receipt: annual.value?.status === 'Covered' ? annual.value.receipt : null,
                    cancelling: isCancelling.value,
                    onConfirm: handleCancel
                }, null, 8 /* PROPS */, ["modelValue", "receipt", "cancelling"])
            ], 64 /* STABLE_FRAGMENT */));
        };
    }
});
